<template>
  <header class="content-listing__filters g-col-4 g-start-1 g-col-700-8 g-col-1000-10 g-start-1000-3" ref="root">
    <div class="content-listing__filter-bar">
      <!-- Search -->
      <div class="content-listing__search-row">
        <h2 class="sr-only">Filter By:</h2>
        <div class="content-listing__search-row-container">
          <div class="content-listing__search-container">
            <input v-model="search_query" type="search" id="post-search" name="q" size="20"
              :placeholder="variation == 'insights-hub' ? 'Search Insights' : variation == 'press-center' ? 'Search Press Releases' : variation == 'search-page' ? 'Search...' : 'Search...'"
              autocomplete="off" :disabled="api_call_status === 1" @keyup="searchKeyUpHandler" @keydown.enter="updateSearch($event)" />
          </div>
        </div>
      </div>

      <!-- Sort -->
      <div v-if="variation != 'insights-hub'" class="content-listing__input-row content-listing__input-row--sort">
        <div role="region" class="content-listing__filter-container js-post-filter-container" aria-label="sort options"
          @keydown="keyDownHandler">
          <button class="content-listing__filter-trigger js-post-filter-trigger" aria-expanded="false"
            aria-labelledby="post-filter-sort" data-taxonomy="sort" @click="toggleFilterList($event)" :disabled="total_items <= 1 || api_call_status === 1">
            <span class="content-listing__filter-trigger-text">{{ getSortButtonText() }}</span>
          </button>
          <div id="post-filter-sort" class="content-listing__filter-list-container" role="region">
            <ul class="content-listing__filter-list">
              <template v-if="variation == 'search-page'">
                <li>
                  <input class="sr-only" type="radio" value="relevance" id="sort-relevance" name="sort"
                    :checked="order_query === 'relevance'" :disabled="api_call_status === 1" @click="updateSort('relevance', $event)" />
                  <label for="sort-relevance">Relevance</label>
                </li>
              </template>
              <li>
                <input class="sr-only" type="radio" value="DESC" id="sort-latest" name="sort"
                  :checked="order_query === 'DESC'" :disabled="api_call_status === 1" @click="updateSort('DESC', $event)" />
                <label for="sort-latest">Latest</label>
              </li>
              <li>
                <input class="sr-only" type="radio" value="ASC" id="sort-oldest" name="sort"
                  :checked="order_query === 'ASC'" :disabled="api_call_status === 1" @click="updateSort('ASC', $event)" />
                <label for="sort-oldest">Oldest</label>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Insight Filters -->
      <template v-if="variation == 'insights-hub'">
        <div class="content-listing__input-row">
          <div v-for="filter in filters.list" :key="filter.label" role="region"
            class="content-listing__filter-container js-post-filter-container" aria-label="filters with selected terms"
            @keydown="keyDownHandler">
            <button class="content-listing__filter-trigger js-post-filter-trigger" aria-expanded="false"
              :aria-labelledby="'post-filter-' + filter.taxonomy" :data-taxonomy="filter.taxonomy"
              @click="toggleFilterList($event)" :disabled="api_call_status === 1">
              <span class="content-listing__filter-trigger-text">{{ getFilterButtonText(filter.taxonomy) }}</span>
            </button>
            <div :id="'post-filter-' + filter.taxonomy" class="content-listing__filter-list-container" role="region">
              <ul class="content-listing__filter-list">
                <li v-for="term in filter.terms" :key="term.id">
                  <input class="sr-only" type="checkbox" :value="term.term_id" :id="term.slug" :name="filter.taxonomy"
                    :checked="isTermSelected(filter.taxonomy, term.term_id)" :disabled="api_call_status === 1" @click="
                      updateTerms(filter.taxonomy, term.term_id, term.name, $event)
                      " />
                  <label :for="term.slug" v-html="term.name"></label>
                </li>
                <li>
                  <button class="canvas-btn canvas-btn--outline"
                    @click="resetTerms(filter.taxonomy, $event)" :disabled="api_call_status === 1">Clear</button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>

      <!-- Submit Button -->
      <template v-if="variation == 'insights-hub'">
        <div class="content-listing__button-row">
          <button class="canvas-btn" @click="submitFilters" :disabled="api_call_status === 1">
            Filter
          </button>
        </div>
      </template>
    </div>

    <!-- No Content Type Filter Tags -->
    <template v-if="variation == 'search-page' && total_items > 0 && content_type_counts.list.length > 0">
      <ul class="content-listing__tag-list">
        <li v-for="type in filteredContentTypes" :key="type.post_type" class="content-listing__tag-item">
          <input class="sr-only" type="checkbox" :id="'post-type-' + type.post_type" :data-post-type="type.post_type"
            :checked="selected_post_type === type.post_type || (!selected_post_type && type.post_type === 'insight,press-release,product,page,post')"
            :disabled="api_call_status === 1" @click="updatePostType(type.post_type, $event)" />
          <label :for="'post-type-' + type.post_type" class="canvas-btn content-listing__tag-button"
            :class="selected_post_type === type.post_type || (!selected_post_type && type.post_type === 'insight,press-release,product,page,post') ? '' : 'canvas-btn--outline'">
            {{ type.name }} ({{ type.count.toLocaleString() }})
          </label>
        </li>
      </ul>
    </template>

  </header>
</template>

<script>
import { ref, reactive, onMounted, toRefs, computed, watch } from "vue";

export default {
  name: "ContentListingBlockFilters",
  props: {
    filters: {
      type: Object,
      required: true,
    },
    content_type_counts: {
      type: Object,
      required: true,
    },
    total_items: {
      type: Number,
      required: true,
    },
    post_type: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    search: {
      type: String,
      required: false,
      default: "",
    },
    variation: {
      type: String,
      required: false,
      default: null,
    },
    api_call_status: {
      type: Number,
      required: true,
    },
    order: {
      type: String,
      required: false,
      default: "DESC",
    },
    orderby: {
      type: String,
      required: false,
      default: "date",
    },
  },
  methods: {
    updatePostType(post_type, e) {
      // console.log(post_type, e);
      this.selected_post_type = post_type;
      this.$emit("update-post-type", post_type);
    },
    resetPostType(e) {
      this.selected_post_type = null;
      this.$emit("reset-post-type");
    },
    toggleFilterList(e) {
      e.preventDefault();
      e.stopPropagation();

      // console.log('Filter button clicked:', e.currentTarget);

      const button = e.currentTarget;
      const isExpanded = button.getAttribute("aria-expanded");
      const newExpandedState = isExpanded === "true" ? "false" : "true";

      // console.log('Current expanded state:', isExpanded, 'New state:', newExpandedState);

      // If we're opening a filter, close all other filters first
      if (newExpandedState === "true") {
        this.hideAllFilterList();
      }

      // Update the aria-expanded attribute
      button.setAttribute("aria-expanded", newExpandedState);

      // Also try to directly manipulate the dropdown visibility as a fallback
      const dropdownContainer = button.nextElementSibling;
      if (dropdownContainer && dropdownContainer.classList.contains('content-listing__filter-list-container')) {
        if (newExpandedState === "true") {
          dropdownContainer.style.display = "block";
        } else {
          dropdownContainer.style.display = "none";
        }
        // console.log('Directly manipulated dropdown visibility:', dropdownContainer.style.display);
      } else {
        // console.log('Could not find dropdown container');
      }
    },
    hideAllFilterList() {
      const filterLists = this.$refs.root.querySelectorAll(".js-post-filter-container");
      filterLists.forEach((list) => {
        const triggerBtn = list.querySelector(".js-post-filter-trigger");
        triggerBtn.setAttribute("aria-expanded", false);
        // Also hide the dropdown directly
        const dropdownContainer = triggerBtn.nextElementSibling;
        if (dropdownContainer && dropdownContainer.classList.contains('content-listing__filter-list-container')) {
          dropdownContainer.style.display = "none";
        }
      });
    },
    updateTerms(taxonomy, term_id, term_name, e) {
      // Update the term selection state
      const filter = this.filters.list.find(f => f.taxonomy === taxonomy);
      if (filter) {
        const term = filter.terms.find(t => t.term_id === term_id);
        if (term) {
          term.selected = e.currentTarget.checked;
        }
      }

      // Update the filter button text to show selected terms
      this.updateFilterButtonText(taxonomy);

      // Auto-submit filters when they change
      this.autoSubmitFilters();
    },
    updateFilterButtonText(taxonomy) {
      const filter = this.filters.list.find(f => f.taxonomy === taxonomy);
      if (filter) {
        const selectedTerms = filter.terms.filter(t => t.selected);
        const trigger = this.$refs.root.querySelector(`[data-taxonomy="${taxonomy}"]`);
        const triggerText = trigger.querySelector('.content-listing__filter-trigger-text');

        if (selectedTerms.length === 0) {
          triggerText.textContent = filter.label;
        } else if (selectedTerms.length === 1) {
          triggerText.textContent = selectedTerms[0].name;
        } else {
          triggerText.textContent = `${filter.label} (${selectedTerms.length})`;
        }
      }
    },
    resetTerms(taxonomy, e) {
      // Reset all terms for this taxonomy
      const filter = this.filters.list.find(f => f.taxonomy === taxonomy);
      if (filter) {
        filter.terms.forEach(term => {
          term.selected = false;
        });
      }

      // Update the filter button text
      this.updateFilterButtonText(taxonomy);

      // Auto-submit filters when they are reset
      this.autoSubmitFilters();
    },
    updateSort(order, e) {
      if (order === 'relevance') {
        this.orderby_query = "relevance";
        this.order_query = order;
      } else {
        this.orderby_query = "date";
        this.order_query = order;
      }

      // Update the sort button text
      this.updateSortButtonText();

      // Close the dropdown
      const button = this.$refs.root.querySelector('[data-taxonomy="sort"]');
      if (button) {
        button.setAttribute("aria-expanded", "false");
        const dropdownContainer = button.nextElementSibling;
        if (dropdownContainer && dropdownContainer.classList.contains('content-listing__filter-list-container')) {
          dropdownContainer.style.display = "none";
        }
      }

      this.$emit("update-sort", this.order_query, this.orderby_query);
    },
    updateSortButtonText() {
      const trigger = this.$refs.root.querySelector('[data-taxonomy="sort"]');
      const triggerText = trigger.querySelector('.content-listing__filter-trigger-text');
      if (triggerText) {
        if (this.order_query === "relevance") {
          triggerText.textContent = "Sort by: Relevance";
        } else {
          triggerText.textContent = `Sort by: ${this.order_query === "DESC" ? "Latest" : "Oldest"}`;
        }
      }
    },
    updateSearch(e) {
      this.$emit("update-search", this.search_query.replace(/[^a-zA-Z0-9\s]/g, ''));
      e.target.blur();
    },
    searchKeyUpHandler() {
      // searchClose.style.display =
      // searchInput.value.length > 0 ? "block" : "none";
    },
    keyDownHandler(e) {
      const trigger = e.currentTarget.querySelector(".js-post-filter-trigger");
      var isExpanded =
        trigger.getAttribute("aria-expanded") === "true" ? true : false;
      if (e.key === "Escape" && isExpanded) {
        trigger.setAttribute("aria-expanded", false);
        trigger.focus();
      }
    },
    getFilterButtonText(taxonomy) {
      const filter = this.filters.list.find(f => f.taxonomy === taxonomy);
      if (filter) {
        const selectedTerms = filter.terms.filter(t => t.selected);
        if (selectedTerms.length === 0) {
          return filter.label;
        } else if (selectedTerms.length === 1) {
          return selectedTerms[0].name;
        } else {
          return `${filter.label} (${selectedTerms.length})`;
        }
      }
      return "Filter";
    },
    isTermSelected(taxonomy, term_id) {
      const filter = this.filters.list.find(f => f.taxonomy === taxonomy);
      if (filter) {
        const term = filter.terms.find(t => t.term_id === term_id);
        return term ? term.selected : false;
      }
      return false;
    },
    submitFilters() {
      // Collect all selected terms from all taxonomies
      const selectedFilters = {};
      this.filters.list.forEach(filter => {
        const selectedTerms = filter.terms.filter(term => term.selected);
        if (selectedTerms.length > 0) {
          selectedFilters[filter.taxonomy] = selectedTerms.map(term => ({
            term_id: term.term_id,
            name: term.name
          }));
        }
      });

      // Emit the submit event with all filter data including search
      this.$emit("submit-filters", {
        search: this.search_query,
        filters: selectedFilters
      });
    },
    autoSubmitFilters() {
      // Collect all selected terms from all taxonomies
      const selectedFilters = {};
      this.filters.list.forEach(filter => {
        const selectedTerms = filter.terms.filter(term => term.selected);
        if (selectedTerms.length > 0) {
          selectedFilters[filter.taxonomy] = selectedTerms.map(term => ({
            term_id: term.term_id,
            name: term.name
          }));
        }
      });

      // Emit the submit event with filter data and search value
      this.$emit("submit-filters", {
        search: this.search_query,
        filters: selectedFilters
      });
    },
    getSortButtonText() {
      if (this.order_query === "relevance") {
        return "Sort by: Relevance";
      }
      return `Sort by: ${this.order_query === "DESC" ? "Latest" : "Oldest"}`;
    },
  },
  setup(props, context) {
    const root = ref(null);
    const filters = reactive(props.filters);
    const { variation, api_call_status } = toRefs(props);
    const content_type_counts = reactive(props.content_type_counts);
    const { total_items } = toRefs(props);

    // Handle post_type prop - convert array to string if needed
    const post_type = computed(() => {
      if (Array.isArray(props.post_type)) {
        return props.post_type.join(',');
      }
      if (props.post_type && typeof props.post_type === 'object' && props.post_type.slug) {
        return props.post_type.slug;
      }
      return props.post_type;
    });

    const selected_post_type = ref(null);
    let search_query = ref("");
    let order_query = ref(props.order);
    let orderby_query = ref(props.orderby);

    // Watch for changes in order and orderby props
    watch(() => props.order, (newOrder) => {
      order_query.value = newOrder;
    });
    
    watch(() => props.orderby, (newOrderby) => {
      orderby_query.value = newOrderby;
    });
    let searchInput;
    let filterLists;
    let parent;
    const selected_term = ref(null);

    // Computed property to filter out pages that do not have template set and exclude product post type
    const filteredContentTypes = computed(() => {
      if (content_type_counts.list && content_type_counts.list.length > 0) {
        return content_type_counts.list.filter(type => {
          // Exclude the "product" post type
          if (type.post_type === 'product') {
            return false;
          }
          // Keep all non-page post types (except product)
          if (type.post_type !== 'page') {
            return true;
          }
          // For pages, only keep those that have a template set
          return type.template !== undefined && type.template !== null;
        });
      }
      return [];
    });
    onMounted(() => {
      searchInput = root.value.querySelector("#post-search");
      filterLists = root.value.querySelectorAll(".js-post-filter-container");
      parent = document.querySelector("#vue-content-listing-block");

      if (props.search) {
        search_query.value = props.search;
      }

      // Initialize filter button texts
      if (props.variation === 'insights-hub' && props.filters && props.filters.list) {
        props.filters.list.forEach(filter => {
          const trigger = root.value.querySelector(`[data-taxonomy="${filter.taxonomy}"]`);
          const triggerText = trigger.querySelector('.content-listing__filter-trigger-text');
          if (triggerText) {
            const selectedTerms = filter.terms.filter(t => t.selected);
            if (selectedTerms.length === 0) {
              triggerText.textContent = filter.label;
            } else if (selectedTerms.length === 1) {
              triggerText.textContent = selectedTerms[0].name;
            } else {
              triggerText.textContent = `${filter.label} (${selectedTerms.length})`;
            }
          }
        });
      }

      // Initialize sort button text for non-insights-hub variations
      if (props.variation !== 'insights-hub') {
        const sortTrigger = root.value.querySelector('[data-taxonomy="sort"]');
        const sortTriggerText = sortTrigger.querySelector('.content-listing__filter-trigger-text');
        if (sortTriggerText) {
          if (order_query.value === "relevance") {
            sortTriggerText.textContent = "Sort by: Relevance";
          } else {
            sortTriggerText.textContent = `Sort by: ${order_query.value === "DESC" ? "Latest" : "Oldest"}`;
          }
        }
      }
    });
    document.addEventListener("click", (e) => {
      const triggers = root.value.querySelectorAll(".js-post-filter-trigger");
      triggers.forEach((trigger) => {
        const isExpanded =
          trigger.getAttribute("aria-expanded") === "true" ? true : false;
        if (
          !trigger.nextSibling.contains(e.target) &&
          !trigger.contains(e.target) &&
          trigger != e.target &&
          isExpanded
        ) {
          trigger.setAttribute("aria-expanded", false);
          // Also hide the dropdown directly
          const dropdownContainer = trigger.nextElementSibling;
          if (dropdownContainer && dropdownContainer.classList.contains('content-listing__filter-list-container')) {
            dropdownContainer.style.display = "none";
          }
        }
      });
    });
    return {
      root,
      filters,
      variation,
      search_query,
      content_type_counts,
      total_items,
      selected_term,
      selected_post_type,
      order_query,
      orderby_query,
      post_type,
      api_call_status,
      filteredContentTypes,
    };
  },
};
</script>
